import React, { useState, useEffect } from 'react';
import { Heart, Sparkles, ArrowRight, Check } from 'lucide-react';
import { 
  fetchAffirmationsByJourney, 
  fetchAffirmationsByTechnique, 
  saveFavoriteAffirmation, 
  saveAffirmationCompletion,
  getDefaultAffirmations 
} from '../shared/api/affirmations';

// ✅ Import centralized theme
import { 
  gradients, 
  buttonStyles, 
  cardStyles, 
  colors,
  shadows 
} from '../shared/config/theme';

const AffirmationCardPack = ({ 
  journeyId, 
  userId, 
  sessionId,
  journeyTitle,
  technique,
  onComplete 
}) => {
  const [affirmations, setAffirmations] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [showCompletion, setShowCompletion] = useState(false);

  useEffect(() => {
    console.log('🎯 AffirmationCardPack initialized with props:', {
      journeyId,
      userId,
      sessionId,
      journeyTitle,
      technique: technique?.name || 'none',
      techniqueId: technique?.id || 'none'
    });
    fetchAffirmationsData();
  }, []);

  const fetchAffirmationsData = async () => {
    try {
      console.log('🔍 Starting affirmations fetch...');

      // Try technique-specific affirmations first
      if (technique && technique.id) {
        console.log('🔍 Fetching technique affirmations for:', technique.id);
        const { affirmations: techniqueAffirmations, error: techniqueError } = 
          await fetchAffirmationsByTechnique(technique.id);

        console.log('📥 Technique API response:', { 
          count: techniqueAffirmations?.length || 0,
          error: techniqueError?.message || 'none',
          data: techniqueAffirmations 
        });

        if (!techniqueError && techniqueAffirmations && techniqueAffirmations.length > 0) {
          console.log('✅ Using technique-specific affirmations:', technique.name);
          const formatted = techniqueAffirmations.map(aff => ({
            id: aff.id,
            affirmation_text: aff.affirmation_text,
            category: aff.category || technique.id,
            journey_id: null,
            technique_id: technique.id
          }));
          console.log('✅ Formatted technique affirmations:', formatted);
          setAffirmations(formatted);
          setLoading(false);
          return;
        }

        console.log('ℹ️ No technique affirmations found, trying journey affirmations');
      }

      // Try journey-specific affirmations
      if (journeyId) {
        console.log('🔍 Fetching journey affirmations for:', journeyId);
        const { affirmations: journeyAffirmations, error: journeyError } = 
          await fetchAffirmationsByJourney(journeyId);

        console.log('📥 Journey API response:', { 
          count: journeyAffirmations?.length || 0,
          error: journeyError?.message || 'none',
          data: journeyAffirmations 
        });

        if (journeyError) {
          console.error('❌ Journey API error:', journeyError);
          throw journeyError;
        }

        if (journeyAffirmations && journeyAffirmations.length > 0) {
          console.log('✅ Using journey affirmations');
          setAffirmations(journeyAffirmations);
          setLoading(false);
          return;
        }
      }

      // Fallback to default affirmations
      console.log('⚠️ No technique or journey affirmations found, using defaults');
      const { affirmations: defaultAffs } = getDefaultAffirmations();
      console.log('📦 Default affirmations loaded:', defaultAffs);
      
      if (defaultAffs && defaultAffs.length > 0) {
        setAffirmations(defaultAffs);
      } else {
        console.error('❌ Even default affirmations are empty!');
        setAffirmations([]);
      }

    } catch (error) {
      console.error('❌ Error fetching affirmations:', error);
      
      // Last resort: use default affirmations
      try {
        const { affirmations: defaultAffs } = getDefaultAffirmations();
        console.log('📦 Fallback to default affirmations:', defaultAffs);
        setAffirmations(defaultAffs || []);
      } catch (defaultError) {
        console.error('❌ Failed to load default affirmations:', defaultError);
        setAffirmations([]);
      }
    } finally {
      setLoading(false);
      console.log('✅ Affirmations fetch complete');
    }
  };

  const handleNext = () => {
    if (currentIndex < affirmations.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleFavorite = async () => {
    const currentAffirmation = affirmations[currentIndex];
    
    if (!currentAffirmation || !currentAffirmation.id) {
      console.error('❌ Cannot favorite: invalid affirmation', currentAffirmation);
      return;
    }
    
    if (favorites.includes(currentAffirmation.id)) {
      setFavorites(prev => prev.filter(id => id !== currentAffirmation.id));
    } else {
      setFavorites(prev => [...prev, currentAffirmation.id]);
      
      const favoriteData = {
        user_id: userId,
        session_id: sessionId,
        affirmation_id: currentAffirmation.id,
        affirmation_text: currentAffirmation.affirmation_text,
        technique_id: technique?.id || null
      };

      const { error } = await saveFavoriteAffirmation(favoriteData);
      if (error) {
        console.error('Error saving favorite:', error);
      }
    }
  };

  const handleFinish = async () => {
    const completionData = {
      user_id: userId,
      session_id: sessionId,
      journey_id: journeyId,
      technique_id: technique?.id || null,
      total_viewed: affirmations.length,
      favorites_count: favorites.length
    };

    const { error } = await saveAffirmationCompletion(completionData);
    if (error) {
      console.error('Error saving affirmation completion:', error);
    }

    setShowCompletion(true);
    setTimeout(() => {
      onComplete();
    }, 2000);
  };

  // Loading state
  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${gradients.background} flex items-center justify-center`}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-gray-700 text-xl">Loading affirmations...</div>
        </div>
      </div>
    );
  }

  // No affirmations state
  if (!affirmations || affirmations.length === 0) {
    console.log('⚠️ Rendering no affirmations screen');
    return (
      <div className={`min-h-screen bg-gradient-to-br ${gradients.background} flex items-center justify-center p-6`}>
        <div className={`${cardStyles.soft.background} border border-peach-200 ${cardStyles.soft.rounded} ${cardStyles.soft.shadow} p-8 text-center max-w-md`}>
          <div className="text-6xl mb-4">💫</div>
          <h2 className="text-gray-900 text-2xl font-bold mb-4">
            No Affirmations Available
          </h2>
          <p className="text-brown-600 mb-6">
            We couldn't load any affirmations for this journey. This might be a temporary issue.
          </p>
          <button
            onClick={onComplete}
            className={`${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} ${buttonStyles.primary.shadow} py-3 px-8`}
          >
            Complete Journey →
          </button>
        </div>
      </div>
    );
  }

  // Validate current affirmation exists
  const currentAffirmation = affirmations[currentIndex];
  if (!currentAffirmation) {
    console.error('❌ No affirmation at current index:', currentIndex);
    return (
      <div className={`min-h-screen bg-gradient-to-br ${gradients.background} flex items-center justify-center p-6`}>
        <div className={`${cardStyles.soft.background} border border-peach-200 ${cardStyles.soft.rounded} ${cardStyles.soft.shadow} p-8 text-center max-w-md`}>
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-gray-900 text-2xl font-bold mb-4">
            Oops! Something went wrong
          </h2>
          <p className="text-brown-600 mb-6">
            There was an issue loading this affirmation.
          </p>
          <button
            onClick={() => setCurrentIndex(0)}
            className={`${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} py-3 px-8 mr-2`}
          >
            Start Over
          </button>
          <button
            onClick={onComplete}
            className={`${buttonStyles.secondary.base} ${buttonStyles.secondary.hover} ${buttonStyles.secondary.transition} ${buttonStyles.secondary.rounded} py-3 px-8`}
          >
            Skip Affirmations
          </button>
        </div>
      </div>
    );
  }

  // Validate affirmation has required text
  if (!currentAffirmation.affirmation_text) {
    console.error('❌ Affirmation missing text:', currentAffirmation);
    return (
      <div className={`min-h-screen bg-gradient-to-br ${gradients.background} flex items-center justify-center p-6`}>
        <div className={`${cardStyles.soft.background} border border-peach-200 ${cardStyles.soft.rounded} ${cardStyles.soft.shadow} p-8 text-center max-w-md`}>
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-gray-900 text-2xl font-bold mb-4">
            Invalid Affirmation Data
          </h2>
          <p className="text-brown-600 mb-6">
            This affirmation is missing required information.
          </p>
          <button
            onClick={handleNext}
            className={`${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} py-3 px-8`}
          >
            {currentIndex < affirmations.length - 1 ? 'Next Affirmation' : 'Complete Journey'}
          </button>
        </div>
      </div>
    );
  }

  const progress = ((currentIndex + 1) / affirmations.length) * 100;

  console.log('🎨 Rendering affirmation:', {
    index: currentIndex,
    total: affirmations.length,
    text: currentAffirmation.affirmation_text
  });

  return (
    <div className={`min-h-screen bg-gradient-to-br ${gradients.background} flex flex-col`}>
      {/* ✅ Completion Modal - Using theme styles */}
      {showCompletion && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} p-8 max-w-md w-full text-center border border-peach-200`}>
            <div className={`w-20 h-20 bg-gradient-to-br ${gradients.button} rounded-full flex items-center justify-center mx-auto mb-4 ${shadows.lg}`}>
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-gray-900 text-2xl font-bold mb-2">
              Journey Complete! 🎉
            </h2>
            <p className="text-brown-700 mb-4">
              You've completed your {technique ? technique.name : journeyTitle} practice
            </p>
            {favorites.length > 0 && (
              <p className="text-orange-600 font-semibold">
                {favorites.length} affirmation{favorites.length !== 1 ? 's' : ''} saved to your favorites
              </p>
            )}
          </div>
        </div>
      )}

      {/* ✅ Header with Progress - Using theme styles */}
      <div className="bg-white/80 backdrop-blur-lg border-b border-peach-200 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-gray-900 font-bold text-lg">
                {technique ? `${technique.icon} ${technique.name}` : 'Daily Affirmations'}
              </h2>
              <p className="text-brown-600 text-sm">{journeyTitle}</p>
            </div>
            <div className="text-orange-600 text-sm font-semibold">
              {currentIndex + 1} / {affirmations.length}
            </div>
          </div>
          
          {/* ✅ Progress Bar - Using theme gradient */}
          <div className="w-full bg-peach-100 rounded-full h-2 overflow-hidden">
            <div 
              className={`bg-gradient-to-r ${gradients.button} h-full transition-all duration-300 ease-out`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* ✅ Main Content Area */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          {/* ✅ Affirmation Card - Using theme card styles */}
          <div className={`${cardStyles.default.background} backdrop-blur-lg ${cardStyles.default.rounded} ${cardStyles.default.shadow} p-12 mb-8 min-h-[400px] flex flex-col items-center justify-center border border-peach-200`}>
            <div className="text-center mb-8">
              <Sparkles className="w-12 h-12 text-orange-500 mx-auto mb-4" />
              <p className="text-gray-900 text-3xl md:text-4xl font-bold leading-relaxed">
                {currentAffirmation.affirmation_text}
              </p>
            </div>

            {/* ✅ Favorite Button - Using theme styles */}
            <button
              onClick={handleFavorite}
              className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 transform hover:scale-105 ${
                favorites.includes(currentAffirmation.id)
                  ? `bg-gradient-to-r ${gradients.button} text-white shadow-lg`
                  : 'bg-peach-100 text-brown-700 hover:bg-peach-200'
              }`}
            >
              <Heart 
                className={`w-5 h-5 ${favorites.includes(currentAffirmation.id) ? 'fill-current' : ''}`}
              />
              {favorites.includes(currentAffirmation.id) ? 'Saved' : 'Save to Favorites'}
            </button>
          </div>

          {/* ✅ Navigation Buttons - Using theme styles */}
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className={`px-6 py-3 ${buttonStyles.secondary.base} ${buttonStyles.secondary.hover} ${buttonStyles.secondary.active} ${buttonStyles.secondary.transition} ${buttonStyles.secondary.rounded} disabled:opacity-30 disabled:cursor-not-allowed`}
            >
              ← Previous
            </button>

            {currentIndex < affirmations.length - 1 ? (
              <button
                onClick={handleNext}
                className={`flex items-center gap-2 px-8 py-3 ${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} ${buttonStyles.primary.shadow} transform`}
              >
                Next
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleFinish}
                className={`flex items-center gap-2 px-8 py-3 ${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} ${buttonStyles.primary.shadow} transform`}
              >
                Complete Journey
                <Sparkles className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* ✅ Helper Text - Using theme colors */}
          <div className="mt-8 text-center">
            <p className="text-brown-600 text-sm">
              💡 Take a deep breath and really feel these words
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffirmationCardPack;